import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  ArtifactKind,
  CategorizedArtifact,
  CategorizedFile,
  CategorizedOther,
  CategorizedSource,
} from "../../utils/categorizeFiles"
import type { DeepFile } from "@/stores/ai-explorer-store"

import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileCode,
  FileText,
  Globe,
  ListTodo,
  NotebookPen,
  Paperclip,
  Sparkles,
} from "@/components/icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { categorizeFiles } from "../../utils/categorizeFiles"

const CODE_EXTENSIONS = new Set(["ts", "tsx", "js", "jsx", "py", "json", "sh", "yaml", "yml"])

/** Iconography for artifact rows. Falls back to a generic file icon. */
function artifactIcon(kind: ArtifactKind): React.ReactNode {
  switch (kind) {
    case "plan":
      return <ListTodo className="text-primary size-3.5 shrink-0" />
    case "deliverable":
      return <Sparkles className="text-primary size-3.5 shrink-0" />
    case "verifications":
      return <CheckCircle2 className="text-success size-3.5 shrink-0" />
    case "scratchpad":
      return <NotebookPen className="text-muted-foreground size-3.5 shrink-0" />
    case "generic":
      return <FileText className="text-muted-foreground size-3.5 shrink-0" />
  }
}

function sourceIcon(): React.ReactNode {
  return <Globe className="text-muted-foreground size-3.5 shrink-0" />
}

function otherIcon(path: string): React.ReactNode {
  const ext = path.split(".").pop()?.toLowerCase() ?? ""
  if (CODE_EXTENSIONS.has(ext)) {
    return <FileCode className="text-muted-foreground size-3.5 shrink-0" />
  }
  return <Paperclip className="text-muted-foreground size-3.5 shrink-0" />
}

function fileBrowserIcon(entry: CategorizedFile | null): React.ReactNode {
  if (!entry) return <FileText className="text-muted-foreground size-3.5 shrink-0" />
  if (entry.category === "artifact") return artifactIcon(entry.kind)
  if (entry.category === "source") return sourceIcon()
  return otherIcon(entry.file.path)
}

interface VirtualFileBrowserProps {
  files: DeepFile[]
  activeFile: string | null
  onFileSelect: (path: string) => void
}

export function VirtualFileBrowser({ files, activeFile, onFileSelect }: VirtualFileBrowserProps) {
  const { t } = useTranslation()

  const categorized = useMemo(() => categorizeFiles(files), [files])
  const { artifacts, sources, other, byPath } = categorized

  const [open, setOpen] = useState(false)
  const [sourcesExpanded, setSourcesExpanded] = useState(false)

  const activeEntry = activeFile ? (byPath.get(activeFile) ?? null) : null

  // If the active file is a source, auto-expand the sources group so the user
  // can see where they are in the list when they open the dropdown.
  useEffect(() => {
    if (activeEntry?.category === "source") {
      setSourcesExpanded(true)
    }
  }, [activeEntry])

  if (files.length === 0) {
    return null
  }

  const triggerLabel =
    activeEntry?.displayName ?? t("deepExplorer.select_file", { defaultValue: "Select file" })

  const triggerIcon = fileBrowserIcon(activeEntry)

  const handleSelect = (path: string) => {
    onFileSelect(path)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t("deepExplorer.virtual_files_aria")}
          title={activeEntry?.file.path ?? triggerLabel}
          type="button"
          className={cn(
            "group flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 text-xs transition-colors",
            "hover:bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            activeEntry ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {triggerIcon}
          <span className="min-w-0 flex-1 truncate text-left">{triggerLabel}</span>
          <ChevronDown
            className={cn(
              "text-muted-foreground size-3.5 shrink-0 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[320px] w-72 overflow-y-auto p-1"
        sideOffset={4}
      >
        <div
          aria-label={t("deepExplorer.virtual_files_aria")}
          className="flex flex-col"
          role="listbox"
        >
          {artifacts.length > 0 && (
            <FileGroup
              label={t("deepExplorer.files_group_artifacts", {
                defaultValue: "Artifacts",
              })}
            >
              {artifacts.map((entry) => (
                <ArtifactRow
                  key={entry.file.path}
                  active={entry.file.path === activeFile}
                  entry={entry}
                  onSelect={handleSelect}
                />
              ))}
            </FileGroup>
          )}

          {sources.length > 0 && (
            <div className={cn(artifacts.length > 0 && "border-border/60 mt-1 border-t pt-1")}>
              <button
                aria-expanded={sourcesExpanded}
                type="button"
                className={cn(
                  "text-muted-foreground hover:text-foreground flex w-full items-center justify-between px-2 py-1 text-[10px] font-medium tracking-wide uppercase transition-colors"
                )}
                onClick={() => setSourcesExpanded((v) => !v)}
              >
                <span className="flex items-center gap-1">
                  {sourcesExpanded ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                  {t("deepExplorer.files_group_sources", { defaultValue: "Sources" })}
                  <span className="text-muted-foreground/70 ml-0.5 tabular-nums">
                    ({sources.length})
                  </span>
                </span>
                {!sourcesExpanded && (
                  <span className="text-muted-foreground/60 text-[10px] normal-case">
                    {t("deepExplorer.files_sources_hint", {
                      defaultValue: "retrieved results",
                    })}
                  </span>
                )}
              </button>
              {sourcesExpanded && (
                <div className="flex flex-col">
                  {sources.map((entry) => (
                    <SourceRow
                      key={entry.file.path}
                      active={entry.file.path === activeFile}
                      entry={entry}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {other.length > 0 && (
            <FileGroup
              label={t("deepExplorer.files_group_other", { defaultValue: "Other" })}
              className={cn(
                (artifacts.length > 0 || sources.length > 0) &&
                  "border-border/60 mt-1 border-t pt-1"
              )}
            >
              {other.map((entry) => (
                <OtherRow
                  key={entry.file.path}
                  active={entry.file.path === activeFile}
                  entry={entry}
                  onSelect={handleSelect}
                />
              ))}
            </FileGroup>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface FileGroupProps {
  label: string
  className?: string
  children: React.ReactNode
}

function FileGroup({ label, className, children }: FileGroupProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-muted-foreground px-2 py-1 text-[10px] font-medium tracking-wide uppercase">
        {label}
      </div>
      {children}
    </div>
  )
}

interface RowBaseProps {
  icon: React.ReactNode
  displayName: string
  path: string
  active: boolean
  onSelect: (path: string) => void
}

function BaseRow({ icon, displayName, path, active, onSelect }: RowBaseProps) {
  return (
    <button
      aria-selected={active}
      role="option"
      title={path}
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        active ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted"
      )}
      onClick={() => onSelect(path)}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{displayName}</span>
      {active && (
        <span aria-hidden="true" className="text-primary text-[10px]">
          ●
        </span>
      )}
    </button>
  )
}

interface ArtifactRowProps {
  entry: CategorizedArtifact
  active: boolean
  onSelect: (path: string) => void
}

function ArtifactRow({ entry, active, onSelect }: ArtifactRowProps) {
  return (
    <BaseRow
      active={active}
      displayName={entry.displayName}
      icon={artifactIcon(entry.kind)}
      path={entry.file.path}
      onSelect={onSelect}
    />
  )
}

interface SourceRowProps {
  entry: CategorizedSource
  active: boolean
  onSelect: (path: string) => void
}

function SourceRow({ entry, active, onSelect }: SourceRowProps) {
  return (
    <BaseRow
      active={active}
      displayName={entry.displayName}
      icon={sourceIcon()}
      path={entry.file.path}
      onSelect={onSelect}
    />
  )
}

interface OtherRowProps {
  entry: CategorizedOther
  active: boolean
  onSelect: (path: string) => void
}

function OtherRow({ entry, active, onSelect }: OtherRowProps) {
  return (
    <BaseRow
      active={active}
      displayName={entry.displayName}
      icon={otherIcon(entry.file.path)}
      path={entry.file.path}
      onSelect={onSelect}
    />
  )
}
